import { useState } from 'react';
import { Button, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { resolveCountryRestrictedMethods } from '@dealstack/shared/lib/common/eideasy/countrySigningMethods';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	Chevron,
	Header,
	MethodCard,
	MethodIcon,
	MethodText,
	Options,
	Wrapper,
} from './MethodSelectStep.styles';
import { METHOD_OPTIONS } from './MethodSelectStep.types';
import { StepHeading, StepSubHeading } from './typography';

interface Props {
	methods: EidEasySigningMethod[];
	onSelectMethod: (method: EidEasySigningMethod) => void;
	/** When true, initially shows only the signer's country-relevant method(s) (or OTP as a fallback), behind a "View all" action. When false/omitted, shows the full `methods` list as-is. */
	restrictToCountry?: boolean;
	countryCode?: string | null;
}

const METHOD_CARD_TEST_IDS: Record<EidEasySigningMethod, string> = {
	[EidEasySigningMethod.OTP]: TEST_ELEMENT_IDS.EID_EASY_METHOD_OTP_CARD,
	[EidEasySigningMethod.DRAW]: TEST_ELEMENT_IDS.EID_EASY_METHOD_DRAW_CARD,
	[EidEasySigningMethod.SMART_ID]: TEST_ELEMENT_IDS.EID_EASY_METHOD_SMART_ID_CARD,
	[EidEasySigningMethod.FTN]: TEST_ELEMENT_IDS.EID_EASY_METHOD_FTN_CARD,
	[EidEasySigningMethod.SE_BANKID]: TEST_ELEMENT_IDS.EID_EASY_METHOD_SE_BANKID_CARD,
	[EidEasySigningMethod.NORWEGIAN_BANKID]: TEST_ELEMENT_IDS.EID_EASY_METHOD_NO_BANKID_CARD,
	[EidEasySigningMethod.MITID]: TEST_ELEMENT_IDS.EID_EASY_METHOD_MITID_CARD,
	[EidEasySigningMethod.FREJA]: TEST_ELEMENT_IDS.EID_EASY_METHOD_FREJA_CARD,
};

const methodCardTestId = (method: EidEasySigningMethod): string | undefined =>
	METHOD_CARD_TEST_IDS[method];

export const MethodSelectStep = ({
	methods,
	onSelectMethod,
	restrictToCountry,
	countryCode,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [showAll, setShowAll] = useState(false);

	const visibleOptions = METHOD_OPTIONS.filter((option) => methods.includes(option.method));

	const initialMethods = restrictToCountry
		? resolveCountryRestrictedMethods(methods, countryCode)
		: methods;
	const displayedOptions =
		restrictToCountry && !showAll
			? METHOD_OPTIONS.filter((option) => initialMethods.includes(option.method))
			: visibleOptions;
	const canViewAll =
		restrictToCountry && !showAll && displayedOptions.length < visibleOptions.length;

	return (
		<Wrapper>
			<Header>
				<StepHeading>{tCommonUi('eid_easy_signing.method_select_heading')}</StepHeading>
				<StepSubHeading>
					{tCommonUi('eid_easy_signing.method_select_subheading')}
				</StepSubHeading>
			</Header>

			<Options
				role="group"
				aria-label={tCommonUi('eid_easy_signing.method_select_heading')}
				data-testid={TEST_ELEMENT_IDS.EID_EASY_METHOD_SELECT_OPTIONS}
			>
				{displayedOptions.map((option) => (
					<MethodCard
						key={option.method}
						type="button"
						onClick={() => onSelectMethod(option.method)}
						data-testid={methodCardTestId(option.method)}
					>
						<MethodIcon>
							<Icon variant={option.icon} size="vlg" />
						</MethodIcon>
						<MethodText>
							<Text fw={500}>{tCommonUi(option.labelKey)}</Text>
							<Text size="xs" c="dimmed">
								{tCommonUi(option.descriptionKey)}
							</Text>
						</MethodText>
						<Chevron aria-hidden>
							<Icon variant="chevron-right" size="lg" />
						</Chevron>
					</MethodCard>
				))}
				{canViewAll && (
					<Button
						variant="subtle"
						onClick={() => setShowAll(true)}
						data-testid={TEST_ELEMENT_IDS.EID_EASY_METHOD_SELECT_VIEW_ALL_BUTTON}
					>
						{tCommonUi('eid_easy_signing.method_select_view_all')}
					</Button>
				)}
			</Options>
		</Wrapper>
	);
};
